import * as React from 'react';

import { cn } from './cn';

// Surface — base paint primitive for tinted boxes. The rest of the
// design system composes Surface for any "rectangle with tone" need:
// Card family (ContentCard / ImageCard / StatCard), info panels (e.g.
// PasswordSetup), dashboard widgets, structural containers, control
// rows (SummaryRow under the indicator plot).
//
// Closed shape — no className / style escape hatches. Visual contract:
//
//   tone:        neutral (default) | muted | primary | secondary
//                | info | success | warning | destructive
//   rounded:     small | medium | large (default)
//   border:      boolean (default true)
//   padded:      boolean (default true → tw-p-4)
//   fullHeight:  boolean (default false → natural height)
//
// Plus interactive / disabled / href / onClick for selectable cells.
//
// Why `rounded` is configurable: unlike Card (always "soft container")
// or Button (always "snappy control"), Surface is intentionally generic
// — the same primitive paints cards, panels, dashboard widgets, AND
// compact control rows. Locking it to a single corner radius would
// force callsites to either mismatch the adjacent control family or
// reach for a different primitive. The discrete scale maps:
//
//     small  → tw-rounded-md (6px)  — for tightly-spaced control rows
//     medium → tw-rounded-lg (8px)  — match Button / IconButton (8px)
//     large  → tw-rounded-xl (12px) — match ContentCard / Dialog / Popover
//
// Default is `large` so the existing card / dialog / panel callsites
// don't change visually. Card composes Surface and pins `rounded`
// explicitly so the intent is recorded at the composition boundary.
//
// Default height is natural — Surface paints itself to its content.
// `fullHeight` adds `tw-h-full` for callsites that need to fill a
// parent with a defined height (dashboard widgets in grid cells,
// AspectRatio media slots, etc.).
//
// Why merge CardSurface + Panel:
//   Both were "tinted box with tone" with cosmetic differences
//   (border-radius, padding, gap) and complementary tone palettes
//   (brand vs status). Splitting the primitive forced the call-site
//   to pick between "container" and "claim" by name; Surface lets
//   the call-site pick tone freely and the composing primitive
//   (Card, PasswordSetup) carries the semantic above it.

const SURFACE_TONE_CONFIG = {
    neutral: {
        surface: 'tw-bg-background tw-text-foreground',
        border: 'tw-border-input',
        // Hover mirrors Button `tone="neutral" appearance="outline"` — border
        // colour stays put, background tints to `bg-muted`. The earlier
        // bright-green border hover (CardSurface's "selectable tile" tell)
        // was inconsistent with how every other control in the design
        // system reacts to hover; we now lean on bg shift alone.
        hover: 'hover:tw-border-input hover:tw-bg-muted hover:tw-text-foreground'
    },
    muted: {
        surface: 'tw-bg-muted tw-text-foreground',
        border: 'tw-border-border',
        hover: null
    },
    primary: {
        surface: 'tw-bg-primary tw-text-primary-foreground',
        border: 'tw-border-primary',
        hover: null
    },
    secondary: {
        surface: 'tw-bg-secondary tw-text-secondary-foreground',
        border: 'tw-border-secondary',
        hover: null
    },
    info: {
        surface: 'tw-bg-info-surface tw-text-info',
        border: 'tw-border-info-border',
        hover: null
    },
    success: {
        surface: 'tw-bg-success-surface tw-text-success',
        border: 'tw-border-success-border',
        hover: null
    },
    warning: {
        surface: 'tw-bg-warning-surface tw-text-warning',
        border: 'tw-border-warning-border',
        hover: null
    },
    destructive: {
        surface: 'tw-bg-destructive-surface tw-text-destructive',
        border: 'tw-border-destructive-border',
        hover: null
    }
};

const SURFACE_BASE_CLASSNAME = 'tw-overflow-hidden tw-transition-colors tw-duration-150';

const SURFACE_ROUNDED_CLASSNAMES = {
    small: 'tw-rounded-md',
    medium: 'tw-rounded-lg',
    large: 'tw-rounded-xl'
};

const resolveSurfaceTone = tone => (
    SURFACE_TONE_CONFIG[tone] ? tone : 'neutral'
);

const resolveSurfaceRounded = rounded => (
    SURFACE_ROUNDED_CLASSNAMES[rounded] ? rounded : 'large'
);

const Surface = React.forwardRef(({
    border = true,
    children,
    className: _className,
    style: _style,
    disabled = false,
    fullHeight = false,
    href,
    interactive = false,
    onClick,
    onKeyDown,
    padded = true,
    rounded = 'large',
    tone = 'neutral',
    ...rest
}, ref) => {
    const resolvedTone = resolveSurfaceTone(tone);
    const cfg = SURFACE_TONE_CONFIG[resolvedTone];
    const resolvedRounded = resolveSurfaceRounded(rounded);
    const isLink = !disabled && href !== undefined;
    const isActionable = !disabled && (href !== undefined || onClick !== undefined);
    const showInteractiveState = !disabled && (interactive || isActionable);

    const handleKeyDown = React.useCallback((event) => {
        onKeyDown?.(event);

        if (event.defaultPrevented || !isActionable || isLink || onClick === undefined) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(event);
        }
    }, [isActionable, isLink, onClick, onKeyDown]);

    const Component = isLink ? 'a' : 'div';

    return (
        <Component
            ref={ref}
            aria-disabled={disabled ? true : undefined}
            className={cn(
                SURFACE_BASE_CLASSNAME,
                SURFACE_ROUNDED_CLASSNAMES[resolvedRounded],
                cfg.surface,
                border ? cn('tw-border tw-border-solid', cfg.border) : null,
                padded ? 'tw-p-4' : null,
                fullHeight ? 'tw-h-full' : null,
                showInteractiveState && cfg.hover ? cfg.hover : null,
                showInteractiveState ? 'tw-group' : null,
                isActionable ? 'tw-cursor-pointer focus-visible:tw-border-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0' : null,
                disabled ? 'tw-cursor-not-allowed tw-opacity-60' : null
            )}
            href={isLink ? href : undefined}
            onClick={isActionable ? onClick : undefined}
            onKeyDown={handleKeyDown}
            role={!isLink && isActionable ? 'button' : undefined}
            tabIndex={!isLink && isActionable ? 0 : undefined}
            {...rest}
        >
            {children}
        </Component>
    );
});

Surface.displayName = 'Surface';

export { Surface };
